/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — stmt-intelligence.ts
   Smart defaults, pattern tracking, suggestions, note field helpers
   ═══════════════════════════════════════════════════════════════════════ */

import { state, type LineItem, type ProjectNotes } from './state';
import { rows, esc, el } from './utils';
import { collectNotesData, cloudSaveUserData } from './data';

// ─── Types ─────────────────────────────────────────────────────────────────

interface PatternData {
  fieldCounts: Record<string, Record<string, number>>;
  projectCount: number;
  lastTracked?: string;
}

interface SmartDefaults {
  [field: string]: string;
}

export interface LineItemRule {
  pattern: RegExp;
  sections: string[];
  field: string;
  title: string;
  msg: string;
}

// ─── Learning / Pattern Intelligence ───────────────────────────────────────

function loadPatterns(): PatternData {
  try {
    return JSON.parse(
      localStorage.getItem('noteflow-patterns-' + state.currentUserEmail) || 'null'
    ) || { fieldCounts: {}, projectCount: 0 };
  } catch (e) {
    return { fieldCounts: {}, projectCount: 0 };
  }
}

function savePatterns(p: PatternData): void {
  localStorage.setItem(
    'noteflow-patterns-' + state.currentUserEmail,
    JSON.stringify(p)
  );
  cloudSaveUserData('patterns', p);
}

export function trackProjectPatterns(): void {
  const patterns: PatternData = loadPatterns();
  const notes: ProjectNotes = collectNotesData();
  patterns.projectCount = (patterns.projectCount || 0) + 1;
  if (!patterns.fieldCounts) patterns.fieldCounts = {};
  Object.keys(notes).forEach((key: string) => {
    const val = String(notes[key] ?? '');
    if (!val) return;
    if (!patterns.fieldCounts[key]) patterns.fieldCounts[key] = {};
    patterns.fieldCounts[key][val] = (patterns.fieldCounts[key][val] || 0) + 1;
  });
  patterns.lastTracked = new Date().toISOString();
  savePatterns(patterns);
}

function getSmartDefaults(): SmartDefaults {
  const patterns: PatternData = loadPatterns();
  const defaults: SmartDefaults = {};
  if ((patterns.projectCount || 0) < 2) return defaults;
  const fc: Record<string, Record<string, number>> = patterns.fieldCounts || {};
  Object.keys(fc).forEach((field: string) => {
    const counts: Record<string, number> = fc[field];
    const total: number = Object.values(counts).reduce((a: number, b: number) => a + b, 0);
    const entries: [string, number][] = Object.entries(counts).sort(
      (a: [string, number], b: [string, number]) => b[1] - a[1]
    );
    if (entries.length > 0 && entries[0][1] / total >= 0.6) {
      defaults[field] = entries[0][0];
    }
  });
  return defaults;
}

export function applySmartDefaults(): void {
  const defaults: SmartDefaults = getSmartDefaults();
  Object.keys(defaults).forEach((id: string) => {
    const fieldEl = document.getElementById(id) as HTMLInputElement | null;
    if (fieldEl && !fieldEl.value) fieldEl.value = defaults[id];
    const radio = document.querySelector(
      'input[name="' + id + '"]'
    ) as HTMLInputElement | null;
    if (radio) {
      const target = document.querySelector(
        'input[name="' + id + '"][value="' + defaults[id] + '"]'
      ) as HTMLInputElement | null;
      if (target) target.checked = true;
    }
  });
}

// ─── Smart Suggestions ─────────────────────────────────────────────────────

const LINE_ITEM_RULES: LineItemRule[] = [
  {
    pattern: /depreciation/i,
    sections: ['opex', 'noncurrent-assets'],
    field: 'nq-depreciation',
    title: 'Depreciation Policy',
    msg: 'Depreciation expense detected on your statements. Ensure the depreciation method and useful lives are disclosed.',
  },
  {
    pattern: /amortization/i,
    sections: ['opex', 'noncurrent-assets'],
    field: 'nq-intangibles',
    title: 'Intangible Assets',
    msg: 'Amortization detected. Consider disclosing intangible asset details and amortization policy.',
  },
  {
    pattern: /lease|right.of.use|rou/i,
    sections: ['current-liab', 'noncurrent-liab', 'noncurrent-assets'],
    field: 'nq-leases',
    title: 'Lease Disclosures (ASC 842)',
    msg: 'Lease-related items detected on your balance sheet. ASC 842 disclosures are likely required.',
  },
  {
    pattern: /loan|note.?payable|mortgage|line.?of.?credit|debt|borrowing/i,
    sections: ['current-liab', 'noncurrent-liab'],
    field: 'nq-debt',
    title: 'Debt Disclosures',
    msg: 'Debt instruments detected in liabilities. Disclose terms, rates, maturity, and any covenants.',
  },
  {
    pattern: /inventor/i,
    sections: ['current-assets'],
    field: 'nq-inventory',
    title: 'Inventory Valuation',
    msg: 'Inventory detected on balance sheet. Disclose the valuation method (FIFO, weighted average, etc.).',
  },
  {
    pattern: /goodwill|intangible|patent|trademark|copyright|franchise|customer.?list/i,
    sections: ['noncurrent-assets'],
    field: 'nq-intangibles',
    title: 'Intangible Assets / Goodwill',
    msg: 'Intangible assets detected. Disclose amortization policy and impairment testing approach.',
  },
  {
    pattern: /receivable/i,
    sections: ['current-assets'],
    field: 'nq-allowance',
    title: 'Accounts Receivable',
    msg: 'Accounts receivable on balance sheet. Consider disclosing allowance for doubtful accounts methodology.',
  },
];

function generateSuggestions(): LineItemRule[] {
  const suggestions: LineItemRule[] = [];
  const dismissed: string[] = JSON.parse(
    localStorage.getItem('noteflow-dismissed-' + state.currentUserEmail) || '[]'
  );

  LINE_ITEM_RULES.forEach((rule: LineItemRule) => {
    if (dismissed.indexOf(rule.field) >= 0) return;
    let found = false;
    rule.sections.forEach((section: string) => {
      if (
        rows(state.currentData, section).some((r: LineItem) =>
          rule.pattern.test(r.label)
        )
      ) {
        found = true;
      }
    });
    if (!found) return;

    // Check if the user has already addressed this field (select, input/textarea, or radio)
    const ruleEl = document.getElementById(rule.field);
    let addressed = false;
    if (ruleEl) {
      if (ruleEl.tagName === 'SELECT' && (ruleEl as HTMLSelectElement).value) addressed = true;
      if ((ruleEl.tagName === 'TEXTAREA' || ruleEl.tagName === 'INPUT') && (ruleEl as HTMLInputElement).value.trim()) addressed = true;
    }
    const radio = document.querySelector(
      'input[name="' + rule.field + '"]:checked'
    ) as HTMLInputElement | null;
    if (radio && radio.value === 'yes') addressed = true;

    if (!addressed) {
      suggestions.push(rule);
    }
  });

  return suggestions;
}

export function renderSuggestions(): void {
  const suggestions: LineItemRule[] = generateSuggestions();
  const container = document.getElementById('note-suggestions');
  if (!container) return;
  if (suggestions.length === 0) {
    container.style.display = 'none';
    return;
  }
  container.style.display = 'block';
  let html = '';
  suggestions.forEach((s: LineItemRule) => {
    html += '<div class="suggestion-card">';
    html += '<div class="sg-title">' + esc(s.title) + '</div>';
    html += '<div class="sg-text">' + esc(s.msg) + '</div>';
    html += '<div class="sg-actions">';
    html +=
      '<button class="sg-btn" data-action="scrollToNoteField" data-param="' +
      s.field +
      '">Address Now</button>';
    html +=
      '<button class="sg-btn" data-action="dismissSuggestion" data-param="' +
      s.field +
      '">Dismiss</button>';
    html += '</div></div>';
  });
  container.innerHTML = html;
}

export function scrollToNoteField(field: string): void {
  let fieldEl: HTMLElement | null = el(field);
  if (!fieldEl) {
    fieldEl = document.querySelector('input[name="' + field + '"]');
  }
  if (fieldEl) {
    fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    fieldEl.focus();
  }
}

export function dismissSuggestion(field: string): void {
  const dismissed: string[] = JSON.parse(
    localStorage.getItem('noteflow-dismissed-' + state.currentUserEmail) || '[]'
  );
  if (dismissed.indexOf(field) === -1) dismissed.push(field);
  localStorage.setItem(
    'noteflow-dismissed-' + state.currentUserEmail,
    JSON.stringify(dismissed)
  );
  cloudSaveUserData('dismissed', dismissed);
  renderSuggestions();
}
